import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { enablePush, pushSupported, currentEndpoint, deviceKind, isStandalone } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bell, BellRing, CheckCircle2, AlertTriangle, Loader2, Mail, Send, Smartphone, MessageCircle } from "lucide-react";

const GUIDES = {
  ios: ["Apri il sito in Safari (iOS 16.4 o successivo)", "Tocca Condividi → \"Aggiungi alla schermata Home\"", "Apri l'app dall'icona sulla Home ed entra in Admin con le tue credenziali", "Premi \"Attiva notifiche\" qui sotto e tocca \"Consenti\"", "Se avevi detto \"Non consentire\": Impostazioni → Notifiche → Centro Auto → Consenti notifiche e Indicatori (badge)"],
  android: ["Apri il sito in Chrome ed entra in Admin", "Menu ⋮ → \"Installa app\" (o \"Aggiungi a schermata Home\")", "Premi \"Attiva notifiche\" qui sotto e tocca \"Consenti\"", "Se bloccate: Impostazioni → App → Centro Auto → Notifiche → attiva"],
  desktop: ["Apri il pannello Admin in Chrome o Edge", "Premi \"Attiva notifiche\" qui sotto e scegli \"Consenti\"", "Per l'icona con il numero installa l'app: icona ⊕ nella barra degli indirizzi", "Se bloccate: clicca il lucchetto accanto all'indirizzo → Notifiche → Consenti"],
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

function StatusRow({ ok, children, testid }) {
  return (
    <div className="flex items-start gap-2 text-sm" data-testid={testid}>
      {ok ? <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />}
      <span>{children}</span>
    </div>
  );
}

function WhatsAppBox({ st, busy, onTest }) {
  const nums = st.whatsapp || [];
  const ready = nums.filter((n) => n.ready);
  const lastBad = (st.whatsapp_last?.results || []).filter((r) => !r.ok);
  return (
    <div className="space-y-2 rounded-xl border border-[#25D366]/25 bg-[#25D366]/5 p-4" data-testid="whatsapp-box">
      <div className="flex items-center gap-2 font-semibold text-sm"><MessageCircle className="w-4 h-4 text-[#25D366]" />Avvisi WhatsApp</div>
      {nums.map((n) => (
        <StatusRow key={n.phone} ok={n.ready} testid={`whatsapp-number-${n.phone.slice(-4)}`}>
          {n.phone.replace("+39", "")}: <b>{n.ready ? "attivo" : "manca il codice di attivazione"}</b>
        </StatusRow>
      ))}
      {st.whatsapp_last && lastBad.length > 0 && <p className="text-xs text-amber-400 break-words" data-testid="whatsapp-last-error">Ultimo invio non riuscito: {lastBad[0].info}</p>}
      {ready.length > 0 ? (
        <Button size="sm" variant="outline" onClick={onTest} disabled={!!busy} className="rounded-full border-[#25D366]/40 text-[#25D366] bg-transparent hover:bg-[#25D366]/10" data-testid="whatsapp-test-btn">
          {busy === "wa" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <MessageCircle className="w-4 h-4 mr-1.5" />}WhatsApp di prova
        </Button>
      ) : null}
      {ready.length < nums.length && (
        <ol className="list-decimal pl-5 space-y-1 text-xs text-muted-foreground" data-testid="whatsapp-guide">
          <li>Apri <b>callmebot.com</b> → "Free API WhatsApp" e salva in rubrica il numero del bot indicato (se dice "bot pieno", riprova tra qualche giorno)</li>
          <li>Da <b>ciascuno</b> dei due telefoni manda al bot su WhatsApp: <i>I allow callmebot to send me messages</i></li>
          <li>Il bot risponde con "Your APIKEY is …": scrivimi in chat che hai i codici e te li faccio inserire in modo sicuro</li>
        </ol>
      )}
    </div>
  );
}

export const NotificationsCenter = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const kind = deviceKind();
  const perm = pushSupported() ? Notification.permission : "unsupported";

  const load = useCallback(async () => {
    const endpoint = await currentEndpoint();
    try { const { data } = await api.get("/push/status", { params: endpoint ? { endpoint } : {} }); setSt(data); } catch (_) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); load(); } };
  const activate = () => run("on", async () => {
    const res = await enablePush();
    if (res.ok) toast.success("Notifiche attivate su questo dispositivo");
    else if (res.reason === "denied") toast.error("Hai bloccato le notifiche: riattivale dalle impostazioni (vedi guida)");
    else if (res.reason === "unsupported") toast.error(kind === "ios" && !isStandalone() ? "Su iPhone apri prima l'app dall'icona sulla Home" : "Questo browser non supporta le notifiche");
    else toast.error("Attivazione non riuscita, riprova");
  });
  const test = () => run("test", async () => {
    const { data } = await api.post("/push/test");
    if (!data.devices) toast.error("Nessun dispositivo registrato: premi prima \"Attiva notifiche\"");
    else if (data.errors?.length) toast.error(`Inviata a ${data.sent}/${data.devices}. Errore: ${data.errors[0]}`);
    else toast.success(`Notifica di prova inviata a ${data.sent} dispositivo/i. Chiudi l'app per vedere il pallino sull'icona.`);
  });
  const testWa = () => run("wa", async () => {
    const { data } = await api.post("/admin/whatsapp/test");
    const bad = data.results.filter((r) => !r.ok);
    if (bad.length) toast.error(`WhatsApp non inviato al numero che finisce con ${bad[0].phone}: ${bad[0].info}`);
    else toast.success(`WhatsApp di prova inviato a ${data.results.length} numero/i`);
  });
  const testEmail = () => run("mail", async () => { const { data } = await api.post("/admin/test-email"); toast.success(`Email di prova inviata a ${data.to}`); });

  const thisOk = !!st?.this_device && perm === "granted";
  return (
    <Dialog onOpenChange={(o) => o && load()}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="rounded-full border-white/15 bg-transparent relative" data-testid="enable-notify-btn" title="Notifiche prenotazioni">
          {thisOk ? <BellRing className="w-4 h-4 text-[#10B981]" /> : <Bell className="w-4 h-4" />}
          {st && !thisOk && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-400" data-testid="notify-warning-dot" />}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg bg-[#12141A] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="notifications-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">Avvisi nuove prenotazioni</DialogTitle></DialogHeader>
        {!st ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#E63946]" /> : (
          <div className="space-y-5">
            <div className="space-y-2 rounded-xl border border-white/10 p-4">
              <StatusRow ok={thisOk} testid="notify-status-this-device">
                Questo dispositivo: <b>{thisOk ? "notifiche attive" : perm === "denied" ? "notifiche bloccate" : "notifiche non attive"}</b>
              </StatusRow>
              <StatusRow ok={st.devices.length > 0} testid="notify-status-devices">
                Dispositivi registrati: <b>{st.devices.length}</b>
                {st.devices.length > 0 && <span className="block text-xs text-muted-foreground">{st.devices.map((d) => d.label).join(" · ")}</span>}
              </StatusRow>
              {st.last && (
                <StatusRow ok={!st.last.errors?.length && st.last.sent > 0} testid="notify-status-last">
                  Ultimo invio ({fmt(st.last.at)}): {st.last.sent}/{st.last.devices} consegnate
                  {st.last.errors?.length > 0 && <span className="block text-xs text-amber-400 break-all">{st.last.errors[0]}</span>}
                </StatusRow>
              )}
              <StatusRow ok={!!st.owner_email} testid="notify-status-email">Email avvisi: <b>{st.owner_email || "non configurata"}</b></StatusRow>
            </div>

            <WhatsAppBox st={st} busy={busy} onTest={testWa} />

            <div className="flex flex-wrap gap-2">
              {!thisOk && (
                <Button onClick={activate} disabled={!!busy} className="rounded-full bg-[#E63946] hover:bg-[#D62828] text-white" data-testid="notify-activate-btn">
                  {busy === "on" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Bell className="w-4 h-4 mr-1.5" />}Attiva notifiche
                </Button>
              )}
              <Button variant="outline" onClick={test} disabled={!!busy} className="rounded-full border-white/15 bg-transparent" data-testid="notify-test-btn">
                {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}Notifica di prova
              </Button>
              <Button variant="outline" onClick={testEmail} disabled={!!busy || !st.owner_email} className="rounded-full border-white/15 bg-transparent" data-testid="notify-test-email-btn">
                {busy === "mail" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Mail className="w-4 h-4 mr-1.5" />}Email di prova
              </Button>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Smartphone className="w-4 h-4 amber" />Come attivarle su {kind === "ios" ? "iPhone" : kind === "android" ? "Android" : "computer"}</div>
              <ol className="list-decimal pl-5 space-y-1 text-xs text-muted-foreground" data-testid="notify-guide">
                {GUIDES[kind].map((g) => <li key={g}>{g}</li>)}
              </ol>
              <p className="text-xs text-muted-foreground mt-3">Ripeti l'attivazione su ogni telefono o computer dove vuoi ricevere gli avvisi. Il numero rosso sull'icona compare quando l'app è installata e si azzera quando apri il pannello.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
